// Lineage:
// packet=60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1
// work_item=WEB-100-004-ai-first-moderation-engine
// generated=60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1:WEB-100-004-ai-first-moderation-engine

import { sql } from "drizzle-orm";
import {
  boolean,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";

export const MODERATION_REASON_CODES = [
  "link_spam",
  "emoji_flood",
  "all_caps",
  "duplicate_message",
  "rate_limit_burst",
  "homoglyph",
  "zero_width",
  "zalgo",
  "raid_pattern",
  "classifier_toxicity",
  "classifier_hate",
  "classifier_sexual",
  "classifier_self_harm",
  "classifier_unavailable_hold",
  "classifier_unavailable_restrict",
  "manual_block",
  "attendee_report_threshold",
] as const;

export type ModerationReasonCode = (typeof MODERATION_REASON_CODES)[number];

export const MODERATION_DECISIONS = [
  "allow",
  "hold",
  "restrict",
  "block",
  "shadow_drop",
] as const;

export type ModerationDecision = (typeof MODERATION_DECISIONS)[number];

export const MODERATION_QUEUE_STATES = [
  "pending",
  "approved",
  "rejected",
  "auto_released",
  "undone",
] as const;

export type ModerationQueueState = (typeof MODERATION_QUEUE_STATES)[number];

export const webinarModerationRules = pgTable(
  "webinar_moderation_rules",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id"),
    ruleKey: text("rule_key").notNull(),
    enabled: boolean("enabled").notNull().default(true),
    threshold: integer("threshold").notNull().default(0),
    windowSeconds: integer("window_seconds").notNull().default(0),
    decision: text("decision").notNull().$type<ModerationDecision>(),
    reasonCode: text("reason_code").notNull().$type<ModerationReasonCode>(),
    config: jsonb("config").notNull().default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    tenantRuleIdx: uniqueIndex("uq_mod_rule_tenant_webinar_key").on(
      t.tenantId,
      t.webinarId,
      t.ruleKey,
    ),
    tenantIdx: index("idx_mod_rule_tenant").on(t.tenantId),
  }),
);

export const webinarModerationQueue = pgTable(
  "webinar_moderation_queue",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    messageId: uuid("message_id").notNull(),
    attendeeId: uuid("attendee_id"),
    decision: text("decision").notNull().$type<ModerationDecision>(),
    reasonCodes: jsonb("reason_codes")
      .notNull()
      .$type<ModerationReasonCode[]>(),
    classifierScore: jsonb("classifier_score").$type<Record<string, number>>(),
    classifierAvailable: boolean("classifier_available").notNull().default(true),
    payload: jsonb("payload").notNull(),
    state: text("state")
      .notNull()
      .$type<ModerationQueueState>()
      .default("pending"),
    heldUntil: timestamp("held_until", { withTimezone: true }),
    resolvedBy: uuid("resolved_by"),
    resolvedAt: timestamp("resolved_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    queueWebinarStateIdx: index("idx_mod_queue_webinar_state").on(
      t.webinarId,
      t.state,
    ),
    queueTenantIdx: index("idx_mod_queue_tenant").on(t.tenantId),
    queueMessageIdx: uniqueIndex("uq_mod_queue_message").on(t.messageId),
  }),
);

export const webinarModerationActions = pgTable(
  "webinar_moderation_actions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    queueId: uuid("queue_id"),
    messageId: uuid("message_id"),
    moderatorId: uuid("moderator_id").notNull(),
    action: text("action").notNull(),
    reasonCode: text("reason_code").$type<ModerationReasonCode>(),
    previousState: text("previous_state"),
    nextState: text("next_state"),
    notes: text("notes"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    actionsWebinarIdx: index("idx_mod_actions_webinar").on(t.webinarId),
    actionsQueueIdx: index("idx_mod_actions_queue").on(t.queueId),
    actionsTenantIdx: index("idx_mod_actions_tenant").on(t.tenantId),
  }),
);

export const webinarAttendeeReports = pgTable(
  "webinar_attendee_reports",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    messageId: uuid("message_id").notNull(),
    reporterId: uuid("reporter_id").notNull(),
    reasonCode: text("reason_code")
      .notNull()
      .$type<ModerationReasonCode>(),
    notes: text("notes"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    reportMessageReporterIdx: uniqueIndex("uq_attendee_report_msg_reporter").on(
      t.messageId,
      t.reporterId,
    ),
    reportWebinarIdx: index("idx_attendee_report_webinar").on(t.webinarId),
  }),
);

export const webinarModerationReports = pgTable(
  "webinar_moderation_reports",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    windowStart: timestamp("window_start", { withTimezone: true }).notNull(),
    windowEnd: timestamp("window_end", { withTimezone: true }).notNull(),
    totalMessages: integer("total_messages").notNull().default(0),
    blockedCount: integer("blocked_count").notNull().default(0),
    heldCount: integer("held_count").notNull().default(0),
    restrictedCount: integer("restricted_count").notNull().default(0),
    classifierUnavailableCount: integer("classifier_unavailable_count")
      .notNull()
      .default(0),
    reasonBreakdown: jsonb("reason_breakdown")
      .notNull()
      .$type<Record<ModerationReasonCode, number>>()
      .default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    reportWebinarWindowIdx: uniqueIndex("uq_mod_report_webinar_window").on(
      t.webinarId,
      t.windowStart,
      t.windowEnd,
    ),
  }),
);

export type WebinarModerationRule = typeof webinarModerationRules.$inferSelect;
export type WebinarModerationQueueRow =
  typeof webinarModerationQueue.$inferSelect;
export type WebinarModerationAction =
  typeof webinarModerationActions.$inferSelect;
export type WebinarAttendeeReport = typeof webinarAttendeeReports.$inferSelect;
export type WebinarModerationReport =
  typeof webinarModerationReports.$inferSelect;
